import { useMemo, useState } from 'react';
import { calculateLayerResistance, calculateUValue } from './calculation/calculate-u-value';
import type { ElementType } from './calculation/types';
import {
  cloneTemplate,
  constructionTemplates,
  elementOptions,
  type CalculatorLayer,
  type ConstructionTemplate,
} from './data/catalog';
import { downloadCalculationPdf } from './pdf/generate-calculation-pdf';

type Step = 1 | 2 | 3 | 4;
type IconName =
  | 'arrow-left'
  | 'arrow-right'
  | 'check'
  | 'chevron'
  | 'floor'
  | 'info'
  | 'layers'
  | 'plus'
  | 'print'
  | 'roof'
  | 'trash'
  | 'wall';

function Icon({ name, size = 20 }: { name: IconName; size?: number }) {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };

  const paths: Record<IconName, React.ReactNode> = {
    'arrow-left': <><path d="m15 18-6-6 6-6" /><path d="M9 12h10" /></>,
    'arrow-right': <><path d="M5 12h14" /><path d="m15 6 6 6-6 6" /></>,
    check: <path d="m5 12 4 4L19 6" />,
    chevron: <path d="m8 10 4 4 4-4" />,
    floor: <><path d="M4 17h16" /><path d="M6 13h12" /><path d="M8 9h8" /><path d="M4 20h16" /></>,
    info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5" /><path d="M12 8h.01" /></>,
    layers: <><path d="m12 3-9 5 9 5 9-5-9-5Z" /><path d="m3 12 9 5 9-5" /><path d="m3 16 9 5 9-5" /></>,
    plus: <><path d="M12 5v14" /><path d="M5 12h14" /></>,
    print: <><path d="M6 9V3h12v6" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><path d="M6 14h12v7H6z" /></>,
    roof: <><path d="m3 12 9-8 9 8" /><path d="M5 10v10h14V10" /><path d="M9 20v-6h6v6" /></>,
    trash: <><path d="M4 7h16" /><path d="M9 7V4h6v3" /><path d="m6 7 1 14h10l1-14" /><path d="M10 11v6M14 11v6" /></>,
    wall: <><path d="M3 4h18v16H3z" /><path d="M3 9h18M3 15h18M8 4v5M16 4v5M6 9v6M14 9v6M9 15v5M18 15v5" /></>,
  };

  return <svg {...common}>{paths[name]}</svg>;
}

function Header() {
  return (
    <header className="site-header">
      <div className="header-inner">
        <a className="brand" href="https://www.sisalwool.com/" aria-label="Sisalwool home">
          <img src="/assets/sisalwool-logo.png" alt="Sisalwool" />
        </a>
        <span className="header-divider" aria-hidden="true" />
        <span className="product-name">U-value calculator</span>
        <a className="back-to-site" href="https://www.sisalwool.com/">
          <Icon name="arrow-left" size={18} />
          <span>Back to Sisalwool</span>
        </a>
      </div>
    </header>
  );
}

const steps = ['Element', 'Construction', 'Build-up', 'Summary'];

function Progress({ step, onNavigate }: { step: Step; onNavigate: (step: Step) => void }) {
  return (
    <nav className="progress" aria-label="Calculator progress">
      <p className="progress-mobile">Step {step} of 4 · {steps[step - 1]}</p>
      <ol>
        {steps.map((label, index) => {
          const number = (index + 1) as Step;
          const complete = number < step;
          const active = number === step;
          const available = number <= step;
          return (
            <li key={label} className={active ? 'active' : complete ? 'complete' : ''}>
              <button
                type="button"
                aria-current={active ? 'step' : undefined}
                disabled={!available || active}
                onClick={() => onNavigate(number)}
              >
                <span className="step-number">{complete ? <Icon name="check" size={16} /> : number}</span>
                <span>{label}</span>
              </button>
              <span className="step-line" aria-hidden="true" />
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function Intro({ eyebrow, title, text }: { eyebrow: string; title: string; text: string }) {
  return (
    <div className="intro">
      <p className="eyebrow">{eyebrow}</p>
      <h1>{title}</h1>
      <p>{text}</p>
    </div>
  );
}

function ElementStep({ onSelect }: { onSelect: (element: ElementType) => void }) {
  return (
    <section>
      <Intro
        eyebrow="Start your calculation"
        title="What are you insulating?"
        text="Choose a building element to see suitable construction build-ups. You can edit the technical inputs before reviewing the result."
      />
      <div className="choice-grid element-grid">
        {elementOptions.map((element) => (
          <button className="choice-card" key={element.id} type="button" onClick={() => onSelect(element.id)}>
            <span className="choice-icon"><Icon name={element.id} size={32} /></span>
            <span className="choice-copy">
              <strong>{element.name}</strong>
              <span>{element.description}</span>
            </span>
            <span className="choice-arrow"><Icon name="arrow-right" /></span>
          </button>
        ))}
      </div>
      <div className="guidance-panel">
        <Icon name="info" size={22} />
        <div>
          <strong>Designed for early-stage guidance</strong>
          <p>This calculator provides an indicative result. Final specifications and regulatory compliance should be verified by a suitably qualified professional.</p>
        </div>
      </div>
    </section>
  );
}

function ConstructionStep({
  element,
  onSelect,
  onBack,
}: {
  element: ElementType;
  onSelect: (template: ConstructionTemplate) => void;
  onBack: () => void;
}) {
  const options = constructionTemplates.filter((template) => template.element === element);
  return (
    <section>
      <Intro
        eyebrow={elementOptions.find((item) => item.id === element)?.name ?? 'Construction'}
        title="Choose a construction"
        text="Select the closest build-up. The next step lets you adjust every relevant thickness and material value."
      />
      <div className="choice-grid construction-grid">
        {options.map((template) => (
          <button className="choice-card construction-card" key={template.id} type="button" onClick={() => onSelect(template)}>
            <span className="choice-icon"><Icon name="layers" size={30} /></span>
            <span className="choice-copy">
              <strong>{template.name}</strong>
              <span>{template.description}</span>
              <small>{template.layers.length} layers · {template.bridge ? 'Repeating timber included' : 'Homogeneous layers'}</small>
            </span>
            <span className="choice-arrow"><Icon name="arrow-right" /></span>
          </button>
        ))}
      </div>
      <div className="step-actions start">
        <button className="button secondary" type="button" onClick={onBack}><Icon name="arrow-left" size={18} />Previous step</button>
      </div>
    </section>
  );
}

function NumberField({
  label,
  value,
  unit,
  min = 0,
  step = 1,
  disabled,
  onChange,
}: {
  label: string;
  value: number;
  unit?: string;
  min?: number;
  step?: number;
  disabled?: boolean;
  onChange?: (value: number) => void;
}) {
  return (
    <label className="number-field">
      <span>{label}</span>
      <span className="input-shell">
        <input
          type="number"
          value={Number.isFinite(value) ? value : ''}
          min={min}
          step={step}
          disabled={disabled}
          onChange={(event) => onChange?.(Number(event.target.value))}
        />
        {unit && <span className="unit">{unit}</span>}
      </span>
    </label>
  );
}

function LayerCard({
  layer,
  resistance,
  onChange,
  onRemove,
}: {
  layer: CalculatorLayer;
  resistance: number;
  onChange: (patch: Partial<CalculatorLayer>) => void;
  onRemove: () => void;
}) {
  return (
    <article className={`layer-card accent-${layer.accent}`}>
      <span className="layer-accent" aria-hidden="true" />
      <div className="layer-identity">
        <span className="field-label">{layer.category}</span>
        <div className="layer-title-row">
          <h3>{layer.name}</h3>
          {layer.accent === 'sisalwool' && <span className="brand-badge">Sisalwool</span>}
        </div>
        {layer.helper && <p className="layer-helper">{layer.helper}</p>}
      </div>
      <div className="layer-fields">
        {layer.thicknessMm !== undefined && (
          <NumberField
            label="Thickness"
            value={layer.thicknessMm}
            unit="mm"
            step={0.5}
            disabled={!layer.editableThickness}
            onChange={(thicknessMm) => onChange({ thicknessMm })}
          />
        )}
        {layer.conductivityWmK !== undefined && (
          <NumberField
            label="Conductivity (λ)"
            value={layer.conductivityWmK}
            unit="W/mK"
            step={0.001}
            disabled={!layer.editableConductivity}
            onChange={(conductivityWmK) => onChange({ conductivityWmK })}
          />
        )}
        <div className="resistance-value">
          <span className="field-label">R-value</span>
          <strong>{resistance.toFixed(3)}</strong>
          <span>m²K/W</span>
        </div>
      </div>
      {layer.removable && (
        <button className="icon-button remove-layer" type="button" onClick={onRemove} aria-label={`Remove ${layer.name}`}>
          <Icon name="trash" size={19} />
        </button>
      )}
    </article>
  );
}

function BridgeEditor({
  template,
  onChange,
}: {
  template: ConstructionTemplate;
  onChange: (widthMm: number, spacingMm: number) => void;
}) {
  if (!template.bridge) return null;
  const fraction = Math.min(1, Math.max(0, template.bridge.widthMm / template.bridge.spacingMm));
  return (
    <section className="bridge-panel">
      <div className="bridge-heading">
        <span className="bridge-icon"><Icon name="wall" size={20} /></span>
        <div>
          <h3>{template.bridge.label}</h3>
          <p>Accounting for {template.bridge.memberName.toLowerCase()} ({(fraction * 100).toFixed(1)}% area).</p>
        </div>
      </div>
      <div className="bridge-fields">
        <NumberField
          label="Timber width"
          value={template.bridge.widthMm}
          unit="mm"
          min={1}
          onChange={(value) => onChange(value, template.bridge!.spacingMm)}
        />
        <NumberField
          label="Centre spacing"
          value={template.bridge.spacingMm}
          unit="mm"
          min={1}
          onChange={(value) => onChange(template.bridge!.widthMm, value)}
        />
        <NumberField
          label="Timber conductivity"
          value={template.bridge.conductivityWmK}
          unit="W/mK"
          step={0.01}
          disabled
        />
      </div>
    </section>
  );
}

type Calculation = ReturnType<typeof calculateUValue>;

function CalculationDetails({ calculation }: { calculation: Calculation }) {
  return (
    <details className="calculation-details">
      <summary>Calculation detail <Icon name="chevron" size={18} /></summary>
      <dl>
        <div><dt>Upper resistance limit</dt><dd>{calculation.upperResistanceM2KW.toFixed(3)} m²K/W</dd></div>
        <div><dt>Lower resistance limit</dt><dd>{calculation.lowerResistanceM2KW.toFixed(3)} m²K/W</dd></div>
        <div><dt>Mean thermal resistance</dt><dd>{calculation.meanResistanceM2KW.toFixed(3)} m²K/W</dd></div>
        <div><dt>Base U-value</dt><dd>{calculation.baseUValueWm2K.toFixed(3)} W/m²K</dd></div>
        <div><dt>Applied corrections</dt><dd>{calculation.correctionsAppliedWm2K.toFixed(3)} W/m²K</dd></div>
      </dl>
    </details>
  );
}

function ResultCard({ calculation }: { calculation: Calculation }) {
  return (
    <aside className="result-column" aria-label="Current calculation result">
      <div className="result-card">
        <div className="result-topline">
          <span>Your U-value</span>
          <span className={`status ${calculation.valid ? 'valid' : ''}`}>
            {calculation.valid && <Icon name="check" size={14} />}
            {calculation.valid ? 'Calculation complete' : 'Check your inputs'}
          </span>
        </div>
        <div className="u-value" aria-live="polite">
          <strong>{calculation.valid ? calculation.displayedUValueWm2K.toFixed(2) : '—'}</strong>
          <span>W/m²K</span>
        </div>
        <div className="result-stat">
          <span>Mean thermal resistance</span>
          <strong>{calculation.meanResistanceM2KW.toFixed(2)} m²K/W</strong>
        </div>
        <p className="result-explainer">A lower U-value indicates less heat loss through the building fabric. Project-specific compliance must be checked separately.</p>
        <CalculationDetails calculation={calculation} />
        <div className="result-disclaimer"><Icon name="info" size={17} /><p>Indicative calculation for design guidance. Verify product values, assumptions and regulatory compliance with a suitably qualified professional.</p></div>
      </div>
      <div className="support-card">
        <h3>Need technical support?</h3>
        <p>The Sisalwool team can help refine your specification for a particular project.</p>
        <a href="https://www.sisalwool.com/pages/contact-us">Speak to a technical adviser <Icon name="arrow-right" size={17} /></a>
      </div>
    </aside>
  );
}

function BuildUpStep({
  template,
  calculation,
  onTemplateChange,
  onBack,
  onContinue,
}: {
  template: ConstructionTemplate;
  calculation: Calculation;
  onTemplateChange: (template: ConstructionTemplate) => void;
  onBack: () => void;
  onContinue: () => void;
}) {
  const updateLayer = (id: string, patch: Partial<CalculatorLayer>) => {
    onTemplateChange({
      ...template,
      layers: template.layers.map((layer) => layer.id === id ? { ...layer, ...patch } : layer),
    });
  };

  const removeLayer = (id: string) => {
    onTemplateChange({ ...template, layers: template.layers.filter((layer) => layer.id !== id) });
  };

  const updateBridge = (widthMm: number, spacingMm: number) => {
    if (!template.bridge) return;
    const safeSpacing = Math.max(1, spacingMm);
    const fraction = Math.min(1, Math.max(0, widthMm / safeSpacing));
    onTemplateChange({
      ...template,
      bridge: { ...template.bridge, widthMm, spacingMm: safeSpacing },
      layers: template.layers.map((layer) =>
        layer.bridge
          ? { ...layer, bridge: { ...layer.bridge, fraction } }
          : layer,
      ),
    });
  };

  const addLayer = () => {
    const id = `custom-${Date.now()}`;
    onTemplateChange({
      ...template,
      layers: [
        ...template.layers,
        {
          id,
          name: 'Additional material layer',
          category: 'Custom layer',
          accent: 'neutral',
          thicknessMm: 12.5,
          conductivityWmK: 0.18,
          editableThickness: true,
          editableConductivity: true,
          removable: true,
        },
      ],
    });
  };

  return (
    <section>
      <Intro
        eyebrow={template.name}
        title="Refine your build-up"
        text="Model the construction from outside to inside. Adjust material thicknesses and technical values to see the live U-value update."
      />
      <div className="calculator-grid">
        <div className="editor-column">
          <div className="orientation-label"><span>Outside</span><span className="orientation-line" /></div>
          <div className="layer-list">
            {template.layers.map((layer) => {
              const calculatedLayer = calculation.layers.find((item) => item.id === layer.id);
              return (
                <div key={layer.id}>
                  <LayerCard
                    layer={layer}
                    resistance={calculatedLayer?.materialResistanceM2KW ?? calculateLayerResistance(layer)}
                    onChange={(patch) => updateLayer(layer.id, patch)}
                    onRemove={() => removeLayer(layer.id)}
                  />
                  {layer.bridge && <BridgeEditor template={template} onChange={updateBridge} />}
                </div>
              );
            })}
          </div>
          <div className="orientation-label inside"><span>Inside</span><span className="orientation-line" /></div>
          <button className="add-layer" type="button" onClick={addLayer}><Icon name="plus" size={19} />Add another layer</button>
          <div className="step-actions">
            <button className="button secondary" type="button" onClick={onBack}><Icon name="arrow-left" size={18} />Previous step</button>
            <button className="button primary" type="button" onClick={onContinue}>Review calculation<Icon name="arrow-right" size={18} /></button>
          </div>
        </div>
        <ResultCard calculation={calculation} />
      </div>
    </section>
  );
}

function SummaryStep({
  template,
  calculation,
  onBack,
  onRestart,
}: {
  template: ConstructionTemplate;
  calculation: Calculation;
  onBack: () => void;
  onRestart: () => void;
}) {
  const [lead, setLead] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    postcode: '',
    customerType: '',
    consent: false,
    website: '',
  });
  const [leadStatus, setLeadStatus] = useState<'idle' | 'submitting' | 'unlocked' | 'error'>('idle');
  const [leadMessage, setLeadMessage] = useState('');

  const createCalculationPayload = () => ({
    construction: template.name,
    uValueWm2K: calculation.finalUValueWm2K,
    meanResistanceM2KW: calculation.meanResistanceM2KW,
    upperResistanceM2KW: calculation.upperResistanceM2KW,
    lowerResistanceM2KW: calculation.lowerResistanceM2KW,
    layers: template.layers.map((layer) => ({
      name: layer.name,
      thicknessMm: layer.thicknessMm,
      conductivityWmK: layer.conductivityWmK,
      resistanceM2KW: calculateLayerResistance(layer),
    })),
  });

  const downloadPdf = async () => {
    try {
      await downloadCalculationPdf(template, calculation, lead.name);
      setLeadMessage('Your PDF download has started.');
    } catch {
      setLeadMessage('The lead was submitted, but the PDF could not be created. Please try the download again.');
    }
  };

  const submitLead = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLeadStatus('submitting');
    setLeadMessage('');

    try {
      if (import.meta.env.DEV) {
        await new Promise((resolve) => window.setTimeout(resolve, 250));
        setLeadStatus('unlocked');
        setLeadMessage('Development preview: the PDF gate was validated without sending an email.');
        await downloadPdf();
        return;
      }

      const response = await fetch('/api/submit-lead.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          ...lead,
          calculation: createCalculationPayload(),
        }),
      });
      const data = await response.json().catch(() => null) as { message?: string } | null;
      if (!response.ok) {
        throw new Error(data?.message || 'Your request could not be sent. Please check the form and try again.');
      }
      setLeadStatus('unlocked');
      setLeadMessage('Thanks — your details have been submitted securely.');
      await downloadPdf();
    } catch (error) {
      setLeadStatus('error');
      setLeadMessage(error instanceof Error ? error.message : 'Your request could not be sent. Please try again.');
    }
  };

  const setLeadField = (field: keyof typeof lead, value: string | boolean) => {
    setLead((current) => ({ ...current, [field]: value }));
  };

  return (
    <section>
      <Intro
        eyebrow="Calculation summary"
        title="Review your result"
        text="Check the build-up, assumptions and resistance limits before sharing or using this indicative calculation."
      />
      <div className="summary-grid">
        <div className="summary-main">
          <section className="summary-card summary-result">
            <div>
              <span>Indicative U-value</span>
              <strong>{calculation.displayedUValueWm2K.toFixed(2)} <small>W/m²K</small></strong>
            </div>
            <span className="status valid"><Icon name="check" size={14} />Calculation complete</span>
          </section>
          <section className="summary-card">
            <p className="eyebrow">Selected construction</p>
            <h2>{template.name}</h2>
            <p>{template.description}</p>
          </section>
          <section className="summary-card">
            <div className="summary-heading"><h2>Construction layers</h2><span>Outside → inside</span></div>
            <ol className="summary-layers">
              {template.layers.map((layer, index) => (
                <li key={layer.id}>
                  <span>{index + 1}</span>
                  <div><strong>{layer.name}</strong><small>{layer.thicknessMm ? `${layer.thicknessMm} mm` : 'Fixed resistance'}{layer.conductivityWmK ? ` · λ ${layer.conductivityWmK} W/mK` : ''}</small></div>
                  <b>{calculateLayerResistance(layer).toFixed(3)} m²K/W</b>
                </li>
              ))}
            </ol>
          </section>
          <section className="summary-card"><h2>Resistance calculation</h2><CalculationDetails calculation={calculation} /></section>
          <div className="summary-warning"><Icon name="info" size={20} /><p><strong>Important:</strong> this is an indicative calculation based on the values entered. It is not a certificate of compliance or a substitute for a project-specific calculation by a qualified professional.</p></div>
          <div className="step-actions start">
            <button className="button secondary" type="button" onClick={onBack}><Icon name="arrow-left" size={18} />Edit build-up</button>
            <button className="button secondary" type="button" onClick={onRestart}><Icon name="plus" size={18} />Start a new calculation</button>
          </div>
        </div>
        <aside className="summary-sidebar" aria-label="Download calculation PDF">
          <section className="summary-card lead-gate" aria-labelledby="pdf-download-title">
            <div className="lead-gate-heading">
              <div>
                <p className="eyebrow">PDF calculation</p>
                <h2 id="pdf-download-title">Download your personal calculation PDF</h2>
                <p>Complete the short form and your branded calculation PDF will download immediately after your request is submitted.</p>
              </div>
              <span className="pdf-icon"><Icon name="print" size={25} /></span>
            </div>

            {leadStatus !== 'unlocked' ? (
              <form className="lead-form" onSubmit={submitLead}>
                <label>
                  <span>Full name <b aria-hidden="true">*</b></span>
                  <input required autoComplete="name" value={lead.name} onChange={(event) => setLeadField('name', event.target.value)} />
                </label>
                <label>
                  <span>Email address <b aria-hidden="true">*</b></span>
                  <input required type="email" autoComplete="email" value={lead.email} onChange={(event) => setLeadField('email', event.target.value)} />
                </label>
                <label>
                  <span>Telephone <b aria-hidden="true">*</b></span>
                  <input required type="tel" autoComplete="tel" value={lead.phone} onChange={(event) => setLeadField('phone', event.target.value)} />
                </label>
                <label>
                  <span>Company</span>
                  <input autoComplete="organization" value={lead.company} onChange={(event) => setLeadField('company', event.target.value)} />
                </label>
                <label>
                  <span>Project postcode or location</span>
                  <input autoComplete="postal-code" value={lead.postcode} onChange={(event) => setLeadField('postcode', event.target.value)} />
                </label>
                <label>
                  <span>Which best describes you? <b aria-hidden="true">*</b></span>
                  <select required value={lead.customerType} onChange={(event) => setLeadField('customerType', event.target.value)}>
                    <option value="">Select an option</option>
                    <option>Homeowner</option>
                    <option>Architect or specifier</option>
                    <option>Builder or contractor</option>
                    <option>Merchant or distributor</option>
                    <option>Other</option>
                  </select>
                </label>
                <label className="honeypot" aria-hidden="true" inert>
                  <span>Website</span>
                  <input tabIndex={-1} autoComplete="off" value={lead.website} onChange={(event) => setLeadField('website', event.target.value)} />
                </label>
                <label className="consent-field">
                  <input required type="checkbox" checked={lead.consent} onChange={(event) => setLeadField('consent', event.target.checked)} />
                  <span>I agree that Sisalwool may use these details to respond to my calculation request. Read the <a href="https://www.sisalwool.com/policies/privacy-policy" target="_blank" rel="noreferrer">privacy policy</a>. <b aria-hidden="true">*</b></span>
                </label>
                {leadMessage && <p className={`form-message ${leadStatus === 'error' ? 'error' : ''}`} role="status">{leadMessage}</p>}
                <button className="button primary lead-submit" type="submit" disabled={leadStatus === 'submitting'}>
                  <Icon name="print" size={18} />
                  {leadStatus === 'submitting' ? 'Sending request…' : 'Submit and download PDF'}
                </button>
                <p className="form-security">Your PDF unlocks only after the request is sent successfully. Required fields are marked with an asterisk.</p>
              </form>
            ) : (
              <div className="download-unlocked" role="status">
                <span><Icon name="check" size={22} /></span>
                <div>
                  <h3>PDF unlocked</h3>
                  <p>{leadMessage}</p>
                  <button className="button primary" type="button" onClick={downloadPdf}><Icon name="print" size={18} />Download PDF again</button>
                </div>
              </div>
            )}
          </section>
        </aside>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div><span>© 2026 Sisalwool Ltd.</span><span>Indicative U-value calculator</span></div>
      <div><a href="https://www.sisalwool.com/policies/privacy-policy">Privacy policy</a><a href="https://www.sisalwool.com/pages/contact-us">Contact</a></div>
    </footer>
  );
}

export default function App() {
  const [step, setStep] = useState<Step>(1);
  const [element, setElement] = useState<ElementType | null>(null);
  const [template, setTemplate] = useState<ConstructionTemplate | null>(null);

  const calculation = useMemo(() => {
    if (!template) return null;
    return calculateUValue({
      externalSurfaceResistanceM2KW: template.externalSurfaceResistanceM2KW,
      internalSurfaceResistanceM2KW: template.internalSurfaceResistanceM2KW,
      layers: template.layers,
    });
  }, [template]);

  const selectElement = (selected: ElementType) => {
    setElement(selected);
    setTemplate(null);
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const selectTemplate = (selected: ConstructionTemplate) => {
    setTemplate(cloneTemplate(selected));
    setStep(3);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const restart = () => {
    setElement(null);
    setTemplate(null);
    setStep(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const navigate = (target: Step) => {
    if (target === 1) restart();
    else if (target === 2 && element) setStep(2);
    else if (target === 3 && template) setStep(3);
  };

  return (
    <div className="app-shell">
      <Header />
      <main className="page-container">
        <Progress step={step} onNavigate={navigate} />
        {step === 1 && <ElementStep onSelect={selectElement} />}
        {step === 2 && element && <ConstructionStep element={element} onSelect={selectTemplate} onBack={restart} />}
        {step === 3 && template && calculation && (
          <BuildUpStep
            template={template}
            calculation={calculation}
            onTemplateChange={setTemplate}
            onBack={() => setStep(2)}
            onContinue={() => { setStep(4); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          />
        )}
        {step === 4 && template && calculation && (
          <SummaryStep template={template} calculation={calculation} onBack={() => setStep(3)} onRestart={restart} />
        )}
      </main>
      <Footer />
    </div>
  );
}
